
<template>
    <div class="container">
        <nav class="nav-secondary" 
            :class="{'all-open': theData.showAll}" 
            v-click-outside="hideAll" 
            role="navigation" 
            aria-label="Secondary Navigation">
            <div v-for="(item, index) in theData.navItems" 
                :key="index" 
                class="nav-secondary__item" 
                :class="{'is-open': item.open, 'is-active': findActiveChild(index)}">
                <template v-if="item.subNav">
                    <button 
                        class="nav-secondary__link" 
                        :class="{'is-active': findActiveChild(index)}" 
                        @click.prevent="handleSecondaryNavClick(index)" 
                        @keydown.esc.exact="closeAllTertiary"
                        @keydown.shift.tab="closeAllTertiary"
                        @keydown.up.exact.prevent="handleSecondaryUp(index)" 
                        @keydown.down.exact.prevent="handleSecondaryDown(index)"
                        @keydown.left.exact.prevent="handleSecondaryPrevious(index, theData.navItems.length)" 
                        @keydown.right.exact.prevent="handleSecondaryNext(index, theData.navItems.length)"
                        :id="'secondary-nav-button-' + index" 
                        :ref="'secondary-dropdown-' + index"
                        aria-haspopup="true"
                        :aria-expanded="item.open ? 'true' : 'false'">{{ item.text}}</button>
                </template>
                <template v-else>
                    <a 
                        :href="item.url" 
                        :target="item.target"
                        :ref="'secondary-dropdown-' + index"
                        @keydown.left.exact.prevent="handleSecondaryPrevious(index, theData.navItems.length)" 
                        @keydown.right.exact.prevent="handleSecondaryNext(index, theData.navItems.length)"
                        class="nav-secondary__link">{{item.text}}</a>
                </template>

                <template v-if="item.subNav">
                    <transition name="slide">
                        <div class="nav-tertiary" 
                            :class="{'is-open': item.open}" 
                            v-if="item.open" 
                            role="menu" 
                            aria-label="Tertiary Navigation" 
                            :aria-labelledby="'secondary-nav-button-' + index">
                            <a v-for="(link, subIndex) in item.subNav" 
                                :key="subIndex" 
                                :href="link.url" 
                                :target="link.target" 
                                class="nav-tertiary__link" 
                                :class="{'is-active': link.active}"
                                @keydown.esc.exact="handleEscape(index)"
                                @keydown.tab.exact="handleTab(subIndex, item.subNav.length)"
                                @keydown.up.exact.prevent="handleTertiaryPrevious(index, item.subNav.length, subIndex)" 
                                @keydown.down.exact.prevent="handleTertiaryNext(index, item.subNav.length, subIndex)"
                                @keydown.left.exact.prevent="handleTertiaryPrevious(index, item.subNav.length, subIndex)" 
                                @keydown.right.exact.prevent="handleTertiaryNext(index, item.subNav.length, subIndex)"
                                v-html="link.text" 
                                :ref="'secondary-dropdown-' + index + '_tertiary-item-' + subIndex" 
                                role="menuitem"></a>
                        </div>
                    </transition>
                </template>
            </div>
        </nav>
    </div>
</template>

<script>
    export default {
        props: {
            navData: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                theData: []
            }
        },
        beforeMount() {
            this.theData = this.navData;

            this.detectActiveItem();
        },
        directives: {
            'click-outside': {
                bind: function (el, binding, vnode) {
                    el.clickOutsideEvent = function (event) {
                    // here I check that click was outside the el and his childrens
                    if (!(el == event.target || el.contains(event.target))) {
                        // and if it did, call method provided in attribute value
                        vnode.context[binding.expression](event);
                    }
                    };
                    document.body.addEventListener('click', el.clickOutsideEvent)
                },
                unbind: function (el) {
                    document.body.removeEventListener('click', el.clickOutsideEvent)
                }
            } // click-outside
        },
        methods: {
            detectActiveItem() {
                // The purpose of this method is to check for the existance of ANY active item.  If any item in any sub-nav is active, we do nothing.  If nothing is active, we need to make the very first item active so that things will work properly on mobile.
                let somethingIsTrue = false;

                // Looping through all navigation items
                for(let i = 0; i < this.theData.navItems.length; i++) {
                    // creating a variable for the current group of subNav items
                    let childGroup = this.theData.navItems[i].subNav;
                    
                    if(childGroup !== "" && childGroup !== undefined && childGroup !== null) {
                        for(let ii = 0; ii < childGroup.length; ii++) {
                            if(childGroup[ii].active === true) {
                                // we found an active item
                                somethingIsTrue = true;
                                // we can stop looping now
                                break;
                            } // end if
                        } // end for

                        if(somethingIsTrue === true) {
                            break;
                        } // end if
                    } // end if
                } // end for

                if(somethingIsTrue !== true) {
                    // There aren't any active items, so let's make the first one active
                    this.theData.navItems[0].subNav[0].active = true;
                }
            },
            showAll() {
                this.theData.showAll = true;
            },
            hideAll() {
                this.theData.showAll = false;
                for(let i = 0; i < this.theData.navItems.length; i++) {
                    this.theData.navItems[i].open = false;
                }
            },
            closeAllTertiary() {
                for(let i = 0; i < this.theData.navItems.length; i++) {
                    this.theData.navItems[i].open = false;
                }
            },
            handleSecondaryNavClick(itemIndex) {
                if(this.theData.showAll === false && window.innerWidth < 992) {
                    this.showAll();
                } else {
                    if(this.theData.navItems[itemIndex].open === true) {
                        this.theData.navItems[itemIndex].open = false;
                    } else {
                        let strRef = 'secondary-dropdown-' + itemIndex + '_tertiary-item-0';
                        this.closeAllTertiary();
                        this.theData.navItems[itemIndex].open = true;

                        this.$nextTick(() => {
                            this.$refs[strRef][0].focus();
                        })
                    }
                }
            },
            findActiveChild(index) {
                // loop through parents children to figure out if this parent is active
                // this.navData.navItems[i].subNav
                let isActive = false;
                let subNav = this.navData.navItems[index].subNav;

                if(subNav !== "" && subNav !== undefined && subNav !== null) {
                    for(let i = 0; i <subNav.length; i++) {
                        let subNavItem = subNav[i];

                        if(subNavItem.active === true) {
                            isActive = subNavItem.active;
                            break;
                        }
                    } // end for loop

                    return isActive;
                } // end if
            }, // end findActiveChild(index)
            handleSecondaryUp(index) {
                let thisItem = this.theData.navItems[index];
                if(thisItem.open) {
                    // pressing up arrow on secondary nav level when tertiary is open shoud close it.
                    this.theData.navItems[index].open = false;
                    let strRef = 'secondary-dropdown-' + index;
                    this.$refs[strRef][0].focus();
                }
            },
            handleSecondaryDown(index) {
                let thisItem = this.theData.navItems[index];
                let strRef = 'secondary-dropdown-' + index + '_tertiary-item-0';
                if(thisItem.open) {
                    // pressing down arrow on secondary nav level when tertiary is open should transfer focus to first link inside tertiary
                    this.$refs[strRef][0].focus();
                } else {
                    // Else it will open the tertiary nav and ensure the first subnav item is focused
                    this.closeAllTertiary();
                    this.theData.navItems[index].open = true;
                    
                    this.$nextTick(() => {
                        this.$refs[strRef][0].focus();
                    })
                }
            },
            // todo here: handle left & right arrow keys for secondary, then handle all directions for tertiary.  excellent resource: https://www.youtube.com/watch?v=SGVSvi1OxE8
            handleEscape(index) {
                let thisItem = this.theData.navItems[index];
                if(thisItem.open) {
                    this.theData.navItems[index].open = false;
                    let strRef = 'secondary-dropdown-' + index;
                    this.$refs[strRef][0].focus();
                }
            },
            handleTab(subIndex, length) {
                if((subIndex + 1 === length)) {
                    this.closeAllTertiary();
                }
            },
            handleSecondaryPrevious(index, length) {
                let thisItem = this.theData.navItems[index];
                let strRef = 'secondary-dropdown-' + (index - 1);

                if(index === 0) {
                    return
                }

                if(thisItem.open) {
                    this.theData.navItems[index].open = false;
                }

                this.$refs[strRef][0].focus();
            },
            handleSecondaryNext(index, length) {
                let thisItem = this.theData.navItems[index];
                let strRef = 'secondary-dropdown-' + (index + 1);

                if((index + 1) === length) {
                    return
                }

                if(thisItem.open) {
                    this.theData.navItems[index].open = false;
                }

                this.$refs[strRef][0].focus();
            },
            handleTertiaryPrevious(index, length, subIndex) {
                let strRef = 'secondary-dropdown-' + index + '_tertiary-item-' + (subIndex - 1);

                if(subIndex === 0) {
                    this.handleSecondaryUp(index);
                    return
                }

                this.$refs[strRef][0].focus();
            },
            handleTertiaryNext(index, length, subIndex) {
                let strRef = 'secondary-dropdown-' + index + '_tertiary-item-' + (subIndex + 1);

                if((subIndex + 1) === length) {
                    return;
                }

                this.$refs[strRef][0].focus();
            },
        }
    }
</script>



// WEBPACK FOOTER //
// src/app/components/phd/nav-secondary-tertiary.vue